import { Hint } from '@synoptic/ui-kit/hint/hint.tsx';
import { themeVars } from '@synoptic/ui-kit/theme/vars.ts';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@synoptic/ui-kit/tooltip/tooltip.tsx';
import { clsx } from 'clsx';
import { ComponentPropsWithoutRef, FC, ReactNode } from 'react';
import {
  statusItem,
  statusItemIcon,
  statusItemTooltipContent,
  statusItemContainer,
} from '@/components/interactive-status/interactive-status.css.ts';

interface InteractiveStatusProps {
  icon: ReactNode;
  variant?: 'active' | 'inactive' | 'error';
  tooltipTitle?: string;
  tooltipDescription?: string;
  tooltipAction?: ReactNode;
  containerClassName?: string;
  iconClassName?: string;
  iconButtonAfter?: ReactNode;
}

const getIconColor = (
  variant: NonNullable<InteractiveStatusProps['variant']>,
  hasText: boolean,
) => {
  switch (variant) {
    case 'active':
      return themeVars.container.foreground.brand;
    case 'inactive': {
      // TODO: don't really like it, not obvious, refactor better maybe or use different component.
      // For icon only cases (sound) disabled color looks like the button is not clickable, primary feels better

      return hasText
        ? themeVars.color.icon.disabled
        : themeVars.container.foreground.secondary;
    }
    case 'error': {
      return themeVars.color.icon.alert.critical;
    }
    default: {
      return themeVars.color.icon.primary;
    }
  }
};

export const InteractiveStatus: FC<
  ComponentPropsWithoutRef<'div'> & InteractiveStatusProps
> = ({
  icon,
  variant = 'active',
  tooltipTitle,
  tooltipDescription,
  tooltipAction,
  children,
  className,
  containerClassName,
  iconClassName,
  iconButtonAfter,
  ...props
}) => {
  const color = getIconColor(variant, Boolean(children));
  const isClickable = Boolean(props.onClick);

  const item = (
    <div
      data-clickable={isClickable ? '' : undefined}
      className={clsx(statusItem, className)}
      {...props}
    >
      <div style={{ color }} className={clsx(statusItemIcon, iconClassName)}>
        {icon}
      </div>
      {children}
    </div>
  );

  return (
    <div className={clsx(statusItemContainer, containerClassName)}>
      {isClickable ? (
        <Tooltip>
          <TooltipTrigger asChild>{item}</TooltipTrigger>
          <TooltipContent
            className={statusItemTooltipContent}
            side={'bottom'}
            title={tooltipTitle}
            description={tooltipDescription}
            action={tooltipAction}
          />
        </Tooltip>
      ) : (
        <Hint
          className={statusItemTooltipContent}
          title={tooltipTitle}
          description={tooltipDescription}
          action={tooltipAction}
          side="bottom"
        >
          {item}
        </Hint>
      )}

      {iconButtonAfter}
    </div>
  );
};
